import i18n from "../i18n";
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PawPrint, Plus } from "lucide-react";
import { toast } from "sonner";
import SpeciesIconPicker from "@/components/SpeciesIconPicker";

export default function SpeciesListPage() {
    const [species, setSpecies] = useState([]);
    const [counts, setCounts] = useState({});
    const [dlg, setDlg] = useState(null);

    const load = useCallback(async () => {
        const s = (await api.get("/species")).data;
        setSpecies(s.filter((x) => x.active !== false));
        const cs = {};
        await Promise.all(s.map(async (sp) => {
            const a = (await api.get(`/animals?species_id=${sp.id}`)).data;
            cs[sp.id] = a.length;
        }));
        setCounts(cs);
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (body) => {
        try {
            await api.post("/species", body);
            toast.success(`${body.name} added`); setDlg(null); load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    };

    return (
        <div className="space-y-8" data-testid="species-list-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="tiny-label">{i18n.t("batch2.d17858483509")}</div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">{i18n.t("batch2.83a7238826ab")}</h1>
                    <p className="text-muted-foreground mt-2 text-lg">
                        {i18n.t("batch2.b37bfabd815c")}
                    </p>
                </div>
                <Button onClick={() => setDlg({})} data-testid="new-species-btn" size="lg">
                    <Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.7ac5949949ba")}
                </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {species.map((sp) => (
                    <Link
                        key={sp.id}
                        to={`/species/${sp.id}`}
                        data-testid={`species-card-${sp.name.toLowerCase()}`}
                    >
                        <Card className="p-6 hover:-translate-y-1 hover:shadow-lg transition-all border rounded-2xl h-full bg-card">
                            <div
                                className="w-14 h-14 rounded-xl grid place-items-center text-3xl mb-4"
                                style={{ backgroundColor: (sp.color || "#e9ecef") + "40" }}
                            >
                                {sp.icon || <PawPrint className="w-7 h-7" />}
                            </div>
                            <div className="font-bold text-xl">{sp.name}</div>
                            <div className="tiny-label mt-2">{counts[sp.id] ?? 0} {i18n.t("batch2.19647db2fbfa")}</div>
                            {sp.gestation_days > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    {i18n.t("batch2.f4ec66c8df43")} {sp.gestation_days} {i18n.t("batch2.5548ae4f34cb")}
                                </div>
                            )}
                        </Card>
                    </Link>
                ))}
                {species.length === 0 && (
                    <Card className="p-8 text-center col-span-full">
                        <div className="text-muted-foreground">{i18n.t("batch2.8c603960eb2f")}</div>
                        <Button className="mt-4" onClick={() => setDlg({})}>
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.ad43a5186744")}
                        </Button>
                    </Card>
                )}
            </div>

            {dlg && <SpeciesQuickDialog initial={dlg} onSave={save} onClose={() => setDlg(null)} />}
        </div>
    );
}

function SpeciesQuickDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", icon: "🐄", color: "#B5A28C", gestation_days: 0, active: true,
        min_sale_age_days: 0, target_weight: 0, price_per_kg: 0, ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="species-quick-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("batch2.7ac5949949ba")}</DialogTitle>
                    <DialogDescription>{i18n.t("batch2.05cc931fe63d")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.57162c35dea4")}</div>
                        <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={i18n.t("batch2.34290c48ee0b")} data-testid="species-quick-name-input" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.716f63b96e0c")}</div>
                        <SpeciesIconPicker value={f.icon} onChange={(icon) => set("icon", icon)} id="quick-species-icon" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.f28563a477a9")}</div>
                            <Input type="color" value={f.color} onChange={(e) => set("color", e.target.value)} className="h-10 p-1" />
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.8f57e24b53b0")}</div>
                            <Input type="number" value={f.gestation_days} onChange={(e) => set("gestation_days", Number(e.target.value) || 0)} />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.17d97685ac11")}</div>
                            <Input type="number" value={f.min_sale_age_days} onChange={(e) => set("min_sale_age_days", Number(e.target.value) || 0)} />
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.4208adadecd9")}</div>
                            <Input type="number" step="0.1" value={f.target_weight} onChange={(e) => set("target_weight", Number(e.target.value) || 0)} />
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">{i18n.t("batch2.3f87a11da0d1")}</div>
                            <Input type="number" step="0.01" value={f.price_per_kg} onChange={(e) => set("price_per_kg", Number(e.target.value) || 0)} />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("batch2.77dfd2135f4d")}</Button>
                    <Button
                        onClick={() => f.name.trim() ? onSave(f) : toast.error("Name required")}
                        data-testid="save-species-quick-btn"
                    >{i18n.t("batch2.ad43a5186744")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
